import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { articleSchema, faqSchema } from "@/lib/schema";
import { mainWebsiteUrl } from "@/mocks/categories";
import { getArticle } from "@/mocks/articles";

function FaqAccordion({ faqs }: { faqs: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="space-y-3">
      {faqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <div
            key={f.q}
            className={`rounded-lg border transition-colors ${
              isOpen ? "border-primary-300 bg-primary-50" : "border-background-200 bg-background-50"
            }`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
            >
              <span className="text-sm font-semibold text-foreground-950 md:text-base">{f.q}</span>
              <i
                className={`ri-add-line w-5 h-5 flex items-center justify-center text-lg text-foreground-500 transition-transform ${
                  isOpen ? "rotate-45" : ""
                }`}
              />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 text-sm leading-relaxed text-foreground-700">{f.a}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Article() {
  const { t, i18n } = useTranslation();
  const { slug = "" } = useParams();
  const article = getArticle(slug, i18n.language);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="flex flex-col items-center justify-center px-4 pt-16 pb-20 text-center">
          <p className="text-lg font-semibold text-foreground-900">Article not found</p>
          <Link to="/" className="mt-4 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
            {t("cta.back")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const { meta, content } = article;

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={articleSchema({
          title: content.title,
          description: content.excerpt,
          image: meta.image,
          slug,
        })}
      />
      <JsonLd data={faqSchema(content.faqs)} />
      <Header />
      <main className="pt-16">
        <article>
          <header className="relative overflow-hidden">
            <img
              src={meta.image}
              alt={content.title}
              title={`${content.title} — USFans Spreadsheet`}
              className="h-64 w-full object-cover object-top md:h-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/50" />
          </header>

          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <nav className="mt-6 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
              <Link to="/" className="hover:text-primary-700 cursor-pointer">
                {t("breadcrumb.home")}
              </Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-base" />
              <span className="text-foreground-900">{content.category}</span>
            </nav>

            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {content.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center text-sm" />
                {t("meta.author")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center text-sm" />
                {t("meta.updated")}
              </span>
            </div>

            <p className="mt-8 text-base leading-relaxed text-foreground-800 md:text-lg">{content.intro}</p>

            {content.sections.map((section) => (
              <div key={section.heading}>
                <h2 className="mt-12 font-heading text-2xl font-bold text-foreground-950">
                  {section.heading}
                </h2>
                <p
                  className={`mt-4 text-base leading-relaxed ${
                    section.highlight
                      ? "rounded-lg border border-accent-200 bg-accent-50 p-5 text-foreground-800"
                      : "text-foreground-700"
                  }`}
                >
                  {section.body}
                </p>
                {section.list && section.list.length > 0 && (
                  <ul className="mt-4 space-y-3">
                    {section.list.map((item, i) => (
                      <li key={item} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">
                          {i + 1}
                        </span>
                        <p className="text-sm leading-relaxed text-foreground-700">{item}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            <h2 className="mt-12 font-heading text-2xl font-bold text-foreground-950">{t("faq.title")}</h2>
            <div className="mt-6">
              <FaqAccordion faqs={content.faqs} />
            </div>

            <h2 className="mt-12 font-heading text-2xl font-bold text-foreground-950">{t("summary.title")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{content.conclusion}</p>

            <div className="mt-12 flex flex-col gap-3 rounded-xl border border-background-200 bg-secondary-50 p-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-700">
                {t("cta.back")} ·{" "}
                <Link to="/" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
                  {t("breadcrumb.home")}
                </Link>
              </p>
              <a
                href={mainWebsiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-base" />
                {t("cta.visit")}
              </a>
            </div>

            <div className="mt-8 pb-16">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center text-base" />
                {t("cta.back")}
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}